import { FocusKeyManager } from '@angular/cdk/a11y';
import { ENTER } from '@angular/cdk/keycodes';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output,
  QueryList, ViewChild, ViewChildren
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { EMPTY, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { GetEntityPublicGQL, SearchEntitiesPublic, SearchEntitiesPublicGQL, UserRole } from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';
import { ListItemComponent } from 'shared/components/list-item/list-item.component';
import { CreateEntityDialog } from 'entity/dialogs/create-entity/create-entity.dialog';
import { MatDialog } from '@angular/material/dialog';
import { AccountRoleService } from 'auth/account-role.service';

@Component({
  selector: 'app-entity-autocomplete',
  templateUrl: './entity-autocomplete.component.html',
  styleUrls: ['./entity-autocomplete.component.scss']
})
export class EntityAutocompleteComponent extends WithDisposableSubject() implements OnInit, OnDestroy {
  /** Output when entity is selected */
  @Output() entitySelected = new EventEmitter<{ id: string; firstName: string; currentAssessmentLevel: string }>();

  /** Filters results to specifc campaign id */
  @Input() campaignId: string;

  /** Filters results to specifc entity type ids */
  @Input() entityTypeIds: string[];

  @Input() labelText: string;

  @Input() excludeEntitiesFn: (results: Array<SearchEntitiesPublic.Nodes>) => SearchEntitiesPublic.Nodes[];

  @Input() allowCreateNew = false;

  get canCreateNew() {
    return this.entityTypeIds?.length === 1 && this.allowCreateNew && this.campaignId
      && this._accountRoleService.userHasMinRole(UserRole.Organizer);
  }

  // <Overlay Related>
  /** Is the dropdown open? */
  isOpen = false;

  /**
   * Reference to trigger to open the select
   * Currently only used in the view
   */
  @ViewChild(CdkOverlayOrigin) trigger: CdkOverlayOrigin;

  /**
   * Used for setting width of overlay
   * to the width of the mat-form-field
   */
  @ViewChild('triggerContainer', { read: ElementRef }) triggerContainer: ElementRef;

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  private _triggerWidth = new Subject<string>();

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  triggerWidth$ = this._triggerWidth.asObservable().pipe(
    takeUntil(this.disposer)
  );

  // </Overlay Related>

  @ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;

  keyManager: FocusKeyManager<ListItemComponent>;

  searchInput = new UntypedFormControl();
  searchResults = [];
  noResults = false;
  loadingResults = false;

  openOverlay() {
    const width = this.triggerContainer?.nativeElement?.offsetWidth;
    this._triggerWidth.next(width ? `${width}px` : '100%');
    this.isOpen = true;
    this._cd.markForCheck();
  }

  closeOverlay() {
    this.isOpen = false;
    this._cd.markForCheck();
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _searchEntitysPublicGQL: SearchEntitiesPublicGQL,
    private _getEntityPublicGQL: GetEntityPublicGQL,
    private _accountRoleService: AccountRoleService,
    private _dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    this.searchInput.valueChanges.pipe(
      takeUntil(this.disposer),
      tap((value) => {
        // If empty value is passed, reset the the searchResults to be empty, and loading to be false, and noResults to be false
        if (isNilOrEmpty(value)) {
          this.searchResults = [];
          this.noResults = false;
          this.loadingResults = false;
        }
      }),
      // Only start to search when length is greater than 2
      filter(value => !isNilOrEmpty(value) && value.length > 2),
      tap(() => this.loadingResults = true),
      debounceTime(400),
      switchMap((value) => this._searchEntitysPublicGQL.fetch({
        keyword: value,
        entityTypeIds: this.entityTypeIds,
        campaignId: this.campaignId,
        assessmentCampaignId: this.campaignId,
        accessibleEntitiesOnly: true
      },
        { fetchPolicy: 'network-only' }
      ).pipe(catchError(() => EMPTY)))
    )
      .subscribe({
        next: (result) => {
          this.openOverlay();
          let searchResults = result.data.searchEntitiesPublic.nodes;
          if (this.excludeEntitiesFn) {
            searchResults = this.excludeEntitiesFn(searchResults);
          }
          this.searchResults = searchResults;
          this.noResults = searchResults.length === 0;
          this.loadingResults = false;
          this.keyManager = new FocusKeyManager(this.items).withWrap();
          this._cd.markForCheck();
        },
        error: (_err) => {
          this.loadingResults = false;
          this._cd.markForCheck();
        }
      });
  }

  setFirstItemActive() {
    this.keyManager.setFirstItemActive();
  }

  onKeydown(event) {
    if (event.keyCode === ENTER) {
      if (this.keyManager.activeItem?.item !== 'create_new') {
        this.onEntitySelected(this.keyManager.activeItem?.item);
      }
      if (this.keyManager.activeItem?.item === 'create_new') {
        this.onCreateNewEntity();
      }

    } else {
      this.keyManager.onKeydown(event);
    }
  }

  onEntitySelected(entity: any) {
    // Set value to null to clear input and close the results.
    this.searchInput.setValue(null);
    this.entitySelected.emit(entity);
    this.closeOverlay();
    this.trigger?.elementRef?.nativeElement?.focus();
  }

  onCreateNewEntity() {
    this.closeOverlay();
    const dref = CreateEntityDialog.open(this._dialog, {
      data: {
        campaignId: this.campaignId,
        initialEntityTypeId: this.entityTypeIds[0]
      }
    });

    dref.afterClosed().pipe(
      filter(result => !!result),
      switchMap((result) => this._getEntityPublicGQL.fetch({
        campaignId: result.campaignContextId,
        entityId: result.id
      }).pipe(
        map(r => r.data.getEntityPublic),
        catchError(() => EMPTY)
      ))
    ).subscribe((entity) => {
      this.onEntitySelected(entity);
    });
  }

}
